import { eevoApiPath } from './api-paths';
import { EEVO_API_KEY_VARIABLE } from './docs/page';
import { ARVO_PRODUCTION_URL } from './public-url';
import {
  ARVO_PUBLIC_MODEL_ID,
  type ArvoDecideRequest,
  type ArvoReportChosenRequest,
  type SystemOneRequest,
} from './types';

/**
 * Code samples for a decision: the native `decisions` endpoint and the SystemOne-compatible one, as
 * curl, Python and TypeScript. Shared by the console's playground ("View as code") and the docs
 * (`./docs/`), so the two cannot describe different requests.
 */

/** The host the public API and its docs name. */
export const ARVO_API_BASE_URL = ARVO_PRODUCTION_URL;

export type SnippetLanguage = 'curl' | 'python' | 'typescript';
export type SnippetEndpoint = 'decide' | 'systemone' | 'chosen';

export const SNIPPET_LANGUAGE_LABELS: Record<SnippetLanguage, string> = {
  curl: 'curl',
  python: 'Python',
  typescript: 'TypeScript',
};

/**
 * The API's URLs on `base`. The docs use the production host (`ARVO_API_BASE_URL`); the console
 * uses the host it runs on, so staging's console points clients at staging
 * (`ui/views/arvo/shared/api-urls.ts`).
 */
export function arvoApiUrls(base: string) {
  return {
    decisions: `${base}${eevoApiPath('/v1/decisions')}`,
    systemone: `${base}${eevoApiPath('/v1/systemone')}`,
    mcp: `${base}/api/arvo/mcp`,
    /** The chosen decision report of a decision; `{id}` is the `dec_…` id the decision returned. */
    chosenTemplate: `${base}${eevoApiPath('/v1/decisions/{id}/chosen')}`,
    assessmentTemplate: `${base}${eevoApiPath('/v1/decisions/{id}/assessments')}`,
  };
}

const PRODUCTION_URLS = arvoApiUrls(ARVO_API_BASE_URL);
export const DECISIONS_URL = PRODUCTION_URLS.decisions;
export const SYSTEMONE_URL = PRODUCTION_URLS.systemone;
export const MCP_URL = PRODUCTION_URLS.mcp;
/** The chosen decision report of a decision; `{id}` is the `dec_…` id the decision returned. */
export const CHOSEN_URL_TEMPLATE = PRODUCTION_URLS.chosenTemplate;
export const ASSESSMENT_URL_TEMPLATE = PRODUCTION_URLS.assessmentTemplate;

export function chosenUrl(decisionId: string, base: string = ARVO_API_BASE_URL): string {
  return arvoApiUrls(base).chosenTemplate.replace('{id}', decisionId);
}

/** The id a snippet shows where the real one comes from the decision's answer. */
export const EXAMPLE_DECISION_ID = 'dec_4e9a1c7b2d8f4a3e9c6b0d1f7a2e5c84';

/**
 * The chosen decision report a snippet shows for `request`: its first option carried out, by an agent. One
 * report covers the whole decision, every stakeholder.
 */
export function exampleChosen(request: ArvoDecideRequest): ArvoReportChosenRequest {
  return {
    chosen_option: request.options[0]?.id ?? 'A',
    decided_by: 'agent',
    note: 'Shipped after the on-call engineer agreed.',
  };
}

/** The question `decide` asks the model, as the SystemOne variant spells it out. */
export const BEST_OPTION_QUESTION = 'Which option is best for the stakeholder?';

/**
 * The same decision as a SystemOne request for ONE stakeholder (SystemOne has one state per request,
 * so a decision with several stakeholders is one SystemOne request per stakeholder; the snippet shows
 * the first): the context and the stakeholder become the state, the options become the criteria of
 * one choice question; the metadata goes along as it is.
 */
export function toSystemOneRequest(request: ArvoDecideRequest, stakeholderIndex = 0): SystemOneRequest {
  const first = request.stakeholders[stakeholderIndex] ?? { description: '' };
  const stakeholder = [
    `STAKEHOLDER (whose interests count): ${first.description}`,
    first.values ? `VALUES: ${first.values}` : null,
  ]
    .filter(line => line !== null)
    .join('\n');
  return {
    model: request.model,
    state: `${request.context}\n\n${stakeholder}`,
    questions: {
      best_option: {
        type: 'choice',
        instructions: BEST_OPTION_QUESTION,
        criteria: Object.fromEntries(request.options.map(option => [option.id, option.description])),
      },
    },
    // An Eevo extension of the SystemOne format: other SystemOne servers ignore it.
    ...(request.metadata ? { metadata: request.metadata } : {}),
  };
}

/**
 * What a snippet sends: the request with `model` as `eevo-1`, the id that always names the latest
 * version, so code copied from it keeps working when a new version is served.
 */
function requestBody(request: ArvoDecideRequest, endpoint: SnippetEndpoint): object {
  if (endpoint === 'chosen') return exampleChosen(request);
  const body = endpoint === 'decide' ? request : toSystemOneRequest(request);
  return { ...body, model: ARVO_PUBLIC_MODEL_ID };
}

function endpointUrl(endpoint: SnippetEndpoint, decisionId: string, base: string): string {
  if (endpoint === 'chosen') return chosenUrl(decisionId, base);
  const urls = arvoApiUrls(base);
  return endpoint === 'decide' ? urls.decisions : urls.systemone;
}

const READ_RESULT: Record<SnippetEndpoint, { python: string; typescript: string }> = {
  decide: {
    python:
      'decision = response.json()\nfor result in decision["results"]:\n    print(result["stakeholder"]["id"], result["best"], result["confidence"])\nprint("agree" if decision["agreement"]["all_same"] else "disagree", decision["id"])',
    typescript: `const decision = await response.json();\nfor (const result of decision.results) console.log(result.stakeholder.id, result.best, result.confidence);\nconsole.log(decision.agreement.all_same ? 'agree' : 'disagree', decision.id);`,
  },
  systemone: {
    python: 'answer = response.json()["answers"]["best_option"]\nprint(answer["choice"], answer["confidence"])',
    typescript: `const { answers } = await response.json();\nconsole.log(answers.best_option.choice, answers.best_option.confidence);`,
  },
  chosen: {
    python: 'chosen = response.json()["chosen"]\nprint(chosen["followedAll"], chosen["followedBy"])',
    typescript: `const row = await response.json();\nconsole.log(row.chosen.followedAll, row.chosen.followedBy);`,
  },
};

/** Single quotes inside a shell single-quoted string are closed, escaped and reopened. */
function shellQuote(text: string): string {
  return `'${text.replace(/'/g, `'\\''`)}'`;
}

/** How a snippet reads the answer of a Default mode decision and of a free-form SystemOne request. */
export const READ_EXPLICIT_RESULT = {
  python:
    'decision = response.json()\nfor result in decision["results"]:\n    print(result["stakeholder"]["id"], result["best"], result["confidence"])\nprint(decision["id"])',
  typescript: `const decision = await response.json();\nfor (const result of decision.results) console.log(result.stakeholder.id, result.best, result.confidence);\nconsole.log(decision.id);`,
};
export const READ_SYSTEMONE_ANSWERS = {
  python: 'for key, answer in response.json()["answers"].items():\n    print(key, answer)',
  typescript: `const { answers } = await response.json();\nfor (const [key, answer] of Object.entries(answers)) console.log(key, answer);`,
};

export function buildSnippet(
  request: ArvoDecideRequest,
  endpoint: SnippetEndpoint,
  language: SnippetLanguage,
  decisionId: string = EXAMPLE_DECISION_ID,
  /** The host the snippet calls: production in the docs, the console's own host in the console. */
  base: string = ARVO_API_BASE_URL,
): string {
  return buildPostSnippet(
    endpointUrl(endpoint, decisionId, base),
    requestBody(request, endpoint),
    language,
    READ_RESULT[endpoint],
  );
}

/** A POST of `body` to `url` with the caller's API key, then `read` on the answer. */
export function buildPostSnippet(
  url: string,
  body: object,
  language: SnippetLanguage,
  read: { python: string; typescript: string },
): string {
  const json = JSON.stringify(body, null, 2);
  switch (language) {
    case 'curl':
      return [
        `curl ${url} \\`,
        `  -H "Authorization: Bearer $${EEVO_API_KEY_VARIABLE}" \\`,
        `  -H "Content-Type: application/json" \\`,
        `  -d ${shellQuote(json)}`,
      ].join('\n');
    case 'python':
      return [
        'import os',
        'import requests',
        '',
        `response = requests.post(`,
        `    "${url}",`,
        `    headers={"Authorization": f"Bearer {os.environ['${EEVO_API_KEY_VARIABLE}']}"},`,
        `    json=${toPythonLiteral(body, 4)},`,
        `    timeout=60,`,
        ')',
        'response.raise_for_status()',
        read.python,
      ].join('\n');
    case 'typescript':
      return [
        `const response = await fetch('${url}', {`,
        `  method: 'POST',`,
        `  headers: {`,
        `    Authorization: \`Bearer \${process.env.${EEVO_API_KEY_VARIABLE}}\`,`,
        `    'Content-Type': 'application/json',`,
        `  },`,
        `  body: JSON.stringify(${indentAfterFirstLine(json, 2)}),`,
        `});`,
        `if (!response.ok) throw new Error(\`Eevo answered \${response.status}\`);`,
        read.typescript,
      ].join('\n');
  }
}

function indentAfterFirstLine(text: string, spaces: number): string {
  const pad = ' '.repeat(spaces);
  return text
    .split('\n')
    .map((line, index) => (index === 0 ? line : pad + line))
    .join('\n');
}

/** JSON as a Python literal: `true`, `false` and `null` outside strings become `True`, `False` and `None`. */
export function toPythonLiteral(value: object, indent: number): string {
  const json = JSON.stringify(value, null, 4).replace(
    /("(?:[^"\\]|\\.)*")|\b(true|false|null)\b/g,
    (_match, quoted: string | undefined, keyword: string | undefined) =>
      quoted ?? (keyword === 'true' ? 'True' : keyword === 'false' ? 'False' : 'None'),
  );
  return indentAfterFirstLine(json, indent);
}
